import { Component, DestroyRef, inject } from '@angular/core';
import { PersonDetailsFormComponent } from '../../components/person-details-form/person-details-form.component';
import { TranslatePipe } from '@ngx-translate/core';
import { UiButtonComponent } from '@ui';
import { PersonService } from '../../../../../../services/person.service';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { PersonModel } from '@models/person.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { personFeature } from '@states/person/person.feature';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, startWith, take } from 'rxjs';
import { PersonActions } from '@states/person/person.actions';
import { Actions, ofType } from '@ngrx/effects';

@Component({
  selector: 'app-edit-person-dialog',
  imports: [
    PersonDetailsFormComponent,
    TranslatePipe,
    UiButtonComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './edit-person-dialog.component.html',
  styleUrl: './edit-person-dialog.component.scss'
})
export class EditPersonDialogComponent {
  private readonly personService = inject(PersonService);
  private readonly dialogRef = inject(MatDialogRef<EditPersonDialogComponent>);
  private readonly store$ = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly person = inject<PersonModel.SavedPerson>(MAT_DIALOG_DATA);

  protected readonly personNameForm = new FormControl<string>(this.person.name ?? '', {
    nonNullable: true,
    validators: [Validators.required],
  });
  protected readonly isUpdating = this.store$.selectSignal(personFeature.selectIsUpdatingSavedPerson);
  protected readonly isPersonNameValid = toSignal(
    this.personNameForm.valueChanges.pipe(
      startWith(this.personNameForm.value),
      map((value) => !!value.trim()),
    ),
    { initialValue: !!this.personNameForm.value.trim() },
  );

  protected getImagePath(person: PersonModel.SavedPerson, gcp: boolean = true): string {
    return this.personService.getPersonImagePath(person.bestImage, true, gcp);
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected save(): void {
    const name = this.personNameForm.value.trim();
    if (!name || this.isUpdating()) {
      return;
    }

    const person = {
      ...this.person,
      name,
    };

    this.actions$
      .pipe(
        ofType(PersonActions.updateSavedPersonSuccess),
        filter((action) => action.person.personId === person.personId),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.close());

    this.store$.dispatch(PersonActions.updateSavedPerson({ person }));
  }
}
